import { useState } from "react";
import {
  assistAdsLibraryDraft,
  type AdsAssistantPostTagDefRow,
  type AdsLibraryAssistDraft,
  type AdsLibraryAssistTarget,
} from "@/lib/adsAssistantClient";
import { AdsLibraryExampleBankAssist } from "@/components/ads-assistant/AdsLibraryExampleBankAssist";

type Props = {
  scopedOrgId: string | null;
  postTagDefs: AdsAssistantPostTagDefRow[];
  onApplyDraft: (draft: AdsLibraryAssistDraft) => void;
  formHasContent: () => boolean;
  applyBankTemplateDraft: (draft: AdsLibraryAssistDraft, selectedPostIds: string[]) => void;
  applyBankCategoryDraft: (draft: AdsLibraryAssistDraft) => void;
};

function labelsFor(ids: string[] | undefined, defs: AdsAssistantPostTagDefRow[]): { id: string; label: string }[] {
  if (!ids?.length) return [];
  const byId = new Map(defs.map((d) => [d.id, d.label] as const));
  return ids
    .map((id) => ({ id, label: byId.get(id) ?? "" }))
    .filter((x) => x.label);
}

export function AdsLibraryAiHelper({
  scopedOrgId,
  postTagDefs,
  onApplyDraft,
  formHasContent,
  applyBankTemplateDraft,
  applyBankCategoryDraft,
}: Props) {
  const [intent, setIntent] = useState("");
  const [pastedAd, setPastedAd] = useState("");
  const [target, setTarget] = useState<AdsLibraryAssistTarget>("auto");
  const [includeSampleAdCopy, setIncludeSampleAdCopy] = useState(true);
  const [sampleAdCopy, setSampleAdCopy] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [appliedArchetypeIds, setAppliedArchetypeIds] = useState<string[]>([]);
  const [suggestedTopicIds, setSuggestedTopicIds] = useState<string[]>([]);

  async function runAssist() {
    setError(null);
    setHint(null);
    setSampleAdCopy("");
    setAppliedArchetypeIds([]);
    setSuggestedTopicIds([]);
    if (!intent.trim() && !pastedAd.trim()) {
      setError("Add a short description, paste an example ad, or both.");
      return;
    }
    if (formHasContent()) {
      const ok = window.confirm(
        "Replace the text in the category, template, or policy fields below with new AI suggestions?"
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      const r = await assistAdsLibraryDraft({
        intent: intent.trim(),
        pastedAd: pastedAd.trim(),
        target,
        includeSampleAdCopy,
      });
      setLoading(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      const { draft, notes: topNotes } = r.data;
      setSampleAdCopy(String(draft.sample_ad_copy || "").trim());
      if (!draft.template && !draft.policy && !draft.category) {
        setHint(topNotes || draft.notes || "No suggestions this time. Try a different description or pick a focus.");
        return;
      }
      onApplyDraft(draft);
      if (draft.template) {
        setAppliedArchetypeIds(draft.template.archetype_tag_ids ?? []);
        setSuggestedTopicIds(draft.template.suggested_topic_tag_ids ?? []);
      }
      setHint(topNotes || draft.notes || "Review the fields below, edit if needed, then click Add.");
    } catch {
      setLoading(false);
      setError("Something went wrong. Try again.");
    }
  }

  return (
    <div className="rounded-xl border border-violet-200/90 bg-gradient-to-b from-violet-50/90 to-white p-4 shadow-sm dark:border-violet-900/60 dark:from-violet-950/40 dark:to-zinc-900">
      <h4 className="text-sm font-semibold text-violet-950 dark:text-violet-100">AI drafting help</h4>
      <p className="mt-1 text-xs text-violet-900/85 dark:text-violet-200/80 max-w-prose leading-relaxed">
        Describe what you want in plain English. For <strong>templates</strong>, suggestions are{" "}
        <strong>reusable format notes</strong> (when to use, structure, tone). You can also ask for{" "}
        <strong>sample ad copy</strong> below for immediate drafting help. The
        assistant fills the forms below; it does <strong>not</strong> save until you click{" "}
        <strong>Add category</strong>, <strong>Add template</strong>, or <strong>Add policy</strong>.
      </p>
      <div className="mt-3 space-y-2">
        <label className="block text-xs font-medium text-violet-900 dark:text-violet-200">
          What are you trying to set up?
        </label>
        <textarea
          className="w-full rounded-lg border border-violet-200/80 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100 min-h-[72px]"
          placeholder='e.g. "I want a template for weekend inventory blowout posts with urgent tone"'
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          disabled={loading}
        />
      </div>
      <div className="mt-3 space-y-2">
        <label className="block text-xs font-medium text-violet-900 dark:text-violet-200">
          Paste example ad copy (optional)
        </label>
        <textarea
          className="w-full rounded-lg border border-violet-200/80 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100 min-h-[64px]"
          placeholder="Paste caption or post text as it would appear on Facebook or Google"
          value={pastedAd}
          onChange={(e) => setPastedAd(e.target.value)}
          disabled={loading}
        />
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <span className="text-xs font-medium text-violet-900 dark:text-violet-200 shrink-0">Focus:</span>
        <select
          className="rounded-lg border border-violet-200/80 bg-white px-2 py-1.5 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
          value={target}
          onChange={(e) => setTarget(e.target.value as AdsLibraryAssistTarget)}
          disabled={loading}
        >
          <option value="auto">Let AI choose</option>
          <option value="template">Template</option>
          <option value="policy">Policy</option>
          <option value="category">Category</option>
          <option value="pricing_guidance">Pricing guidance</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-violet-900 dark:text-violet-200">
          <input
            type="checkbox"
            checked={includeSampleAdCopy}
            onChange={(e) => setIncludeSampleAdCopy(e.target.checked)}
            disabled={loading}
          />
          Include sample ad copy
        </label>
        <button
          type="button"
          onClick={() => void runAssist()}
          disabled={loading}
          className="rounded-lg bg-violet-700 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-800 disabled:opacity-50 sm:ml-auto"
        >
          {loading ? "Working…" : "Suggest fields"}
        </button>
      </div>
      {error ? (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p className="mt-3 rounded-lg border border-emerald-200/80 bg-emerald-50/80 px-3 py-2 text-sm text-emerald-950 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-100">
          {hint}
        </p>
      ) : null}
      {sampleAdCopy ? (
        <div className="mt-3 rounded-lg border border-violet-200/80 bg-violet-50/70 px-3 py-2 text-sm text-violet-950 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-100">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide">Sample ad copy</div>
          <pre className="whitespace-pre-wrap text-xs leading-relaxed">{sampleAdCopy}</pre>
          <button
            type="button"
            className="mt-2 rounded border border-violet-300 bg-white px-2 py-1 text-xs text-violet-800 hover:bg-violet-100 dark:border-violet-800 dark:bg-zinc-900 dark:text-violet-200 dark:hover:bg-violet-950/60"
            onClick={() => void navigator.clipboard.writeText(sampleAdCopy)}
          >
            Copy sample copy
          </button>
        </div>
      ) : null}
      {appliedArchetypeIds.length || suggestedTopicIds.length ? (
        <div className="mt-3 rounded-lg border border-violet-200/80 bg-white px-3 py-2 text-xs text-violet-950 dark:border-violet-900/50 dark:bg-zinc-900 dark:text-violet-100">
          {appliedArchetypeIds.length ? (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide">Applied archetype tags</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {labelsFor(appliedArchetypeIds, postTagDefs).map((c) => (
                  <span
                    key={c.id}
                    className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                  >
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {suggestedTopicIds.length ? (
            <div className={appliedArchetypeIds.length ? "mt-2" : ""}>
              <div className="text-[11px] font-semibold uppercase tracking-wide">
                Often pairs with topic tags
              </div>
              <p className="mt-1 text-[10px] text-violet-900/80 dark:text-violet-200/80">
                Preview only — apply these when you log real posts later.
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {labelsFor(suggestedTopicIds, postTagDefs).map((c) => (
                  <span
                    key={c.id}
                    className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800 dark:bg-green-900/40 dark:text-green-200"
                  >
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <details className="mt-4 rounded-lg border border-violet-300/60 bg-violet-100/40 dark:border-violet-800/50 dark:bg-violet-950/20">
        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-violet-950 dark:text-violet-100 select-none">
          Suggest from saved ads (example bank)
        </summary>
        <div className="border-t border-violet-200/60 px-2 pb-3 pt-2 dark:border-violet-900/40">
          <AdsLibraryExampleBankAssist
            scopedOrgId={scopedOrgId}
            onApplyTemplateSuggestion={applyBankTemplateDraft}
            onApplyCategorySuggestion={applyBankCategoryDraft}
          />
        </div>
      </details>
    </div>
  );
}
